import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FieldTypeConfig } from '@ngx-formly/core';
import { AnalyticsAwareFieldType } from '../analytics-aware-field-type';
import { Subscription, finalize } from 'rxjs';
import { IdInputStatusEnum } from '../../../models/irembo-id-input-status.enum';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { IEnvironment } from '../../../models/environment.model';

import {
  checkForValidFields,
  configureFields,
  populateFormFields,
  updateUrlWithApiGatewayBaseUrl,
  getPopulateReferenceForm,
  subscribeToResetFieldFetchData,
} from '../../../../utils/utils/data-fetch-widget-utils';
import { FormStateService } from '../../../services/formly/form-state.service';
import { v4 as uuidv4 } from 'uuid';
import { EIremboFormlyFormStateKeys } from '../../../models/irembo-formly-formstate-keys.enum';
import { IHttpPagedResponse } from '../../../../../public-api';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GenericDPPConfig } from '../../../models/generic-data-fetch-dpp-config.model';

const INTEGRATION_URL = '/integration/v1/fetch/sync';

@Component({
  selector: 'irembogov-custom-tin-input',
  templateUrl: './custom-tin-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomTinInputComponent
  extends AnalyticsAwareFieldType<FieldTypeConfig>
  implements OnDestroy, AfterViewInit
{
  private environment: IEnvironment;
  private subscriptions = new Subscription();
  statusClass: string | undefined;

  fieldsToPopulate: Record<string, string>[] = [];

  tinLength = 9;
  useBaseUrl = true;
  isReadonly = false;
  usePrefetch = false;
  applicationBusinessId: string | undefined;
  applicantUserId: string | undefined;

  // --- DPP State Variables ---
  @ViewChild('dppVerificationModal') dppVerificationModal!: TemplateRef<any>;
  dppConfig?: GenericDPPConfig;
  dppInputControl = new FormControl('', [Validators.required]);
  dppError?: string;
  private originalFetchPayload?: Record<string, unknown>;
  isVerifyingDPP = false;

  constructor(
    @Inject('environment') environment: IEnvironment,
    private http: HttpClient,
    private formStateService: FormStateService,
    private cd: ChangeDetectorRef,
    private modalService: NgbModal
  ) {
    super();
    this.environment = environment;
  }

  ngAfterViewInit(): void {
    if (this.field.props['tinLength'] !== undefined) {
      this.tinLength = this.field.props['tinLength'];
    }

    if (this.field.props['useBaseUrl'] !== undefined) {
      this.useBaseUrl = Boolean(this.field.props['useBaseUrl']);
    }

    if (this.field.props['usePrefetch']) {
      this.usePrefetch = Boolean(this.field.props['usePrefetch']);
    }

    this.subscriptions.add(
      subscribeToResetFieldFetchData(
        this.field,
        this.formStateService,
        this.fieldsToPopulate,
        getPopulateReferenceForm(this.field, this.form)
      )
    );

    checkForValidFields(this.field);
    this.fieldsToPopulate = this.field.props?.['populates'] ?? [];
    const formRef = getPopulateReferenceForm(this.field, this.form);

    if (this.fieldsToPopulate.length > 0) {
      this.subscriptions.add(
        configureFields(this.field, this.fieldsToPopulate, formRef)
      );
    }

    this.applicationBusinessId =
      this.formState[EIremboFormlyFormStateKeys.APPLICANT_BUSINESS_ID];
    this.applicantUserId =
      this.formState[EIremboFormlyFormStateKeys.APPLICANT_USER_ID];

    if (this.usePrefetch && this.applicationBusinessId) {
      this.isReadonly = true;
      this.getTINFromPrefetchUrl();
    } else {
      this.isReadonly = false;
    }

    this.clearTinDefaultValue();

    this.subscriptions.add(
      this.field.options?.fieldChanges?.subscribe(() => {
        this.clearTinDefaultValue();
      })
    );
  }

  clearTinDefaultValue(): void {
    if (this.field.defaultValue) {
      this.field.defaultValue = undefined;
    }
  }

  onVerifyTin(tin: string) {
    let { endpointCode } = this.field.props;

    const request_url = updateUrlWithApiGatewayBaseUrl(
      INTEGRATION_URL,
      this.environment
    );

    if (!endpointCode) {
      const defaultEndPointCode = this.environment.endpointCodes?.companyTin;
      if (!defaultEndPointCode) {
        throw new Error('Endpoint code is missing');
      }
      endpointCode = defaultEndPointCode;
    }

    const payload: Record<string, unknown> = { tinNumber: tin };
    this.dppConfig = this.field.props['dppConfig'] as GenericDPPConfig;

    // Inject DPP configs into the payload
    if (this.dppConfig?.enabled) {
      payload['__dppRequested'] = true;
      payload['__dppResponseProperty'] = this.dppConfig.responseProperty;
      payload['__dppExtractProperty'] = this.dppConfig.extractProperty;
      payload['__dppMatchType'] = this.dppConfig.matchType || 'contains';
    }

    const params = {
      endpointCode,
      callerId: uuidv4(),
      payload,
      requester: uuidv4(),
    };

    this.statusClass = 'fetching';
    this.subscriptions.add(
      this.http.post<Record<string, unknown>>(request_url, params).subscribe({
        next: (res: Record<string, unknown>) => {
          const dppStatus = this.checkDppStatus(res);

          // INTERCEPT the specific security requirement from the SUCCESS response
          if (
            this.dppConfig?.enabled &&
            dppStatus === 'DATA_REQUIRE_VALIDATION'
          ) {
            this.originalFetchPayload = payload;
            this.dppInputControl.reset();
            this.dppError = undefined;
            this.statusClass = undefined; // Reset spinner on input field
            this.cd.detectChanges();

            this.modalService
              .open(this.dppVerificationModal, {
                centered: true,
                backdrop: 'static',
              })
              .result.then(
                () => {
                  /* Handled automatically on verify success */
                },
                () => {
                  // User clicks cancel modal
                  this.statusClass = IdInputStatusEnum.DANGER;
                  this.cd.detectChanges();
                  this.emitFetchOutcome('failure', { code: 'DPP_CANCELLED' });
                }
              );
            return;
          }

          this.processSuccessfulFetch(res);
        },
        error: (error: any) => {
          this.statusClass = IdInputStatusEnum.DANGER;
          this.cd.detectChanges();
          this.emitFetchOutcome('failure', error);
        },
      })
    );
  }

  executeDPPVerifyRequest(modal: any) {
    if (
      !this.dppConfig ||
      !this.originalFetchPayload ||
      this.dppInputControl.invalid
    ) {
      return;
    }

    this.isVerifyingDPP = true;
    this.dppError = undefined;
    this.cd.detectChanges();

    const request_url = updateUrlWithApiGatewayBaseUrl(
      INTEGRATION_URL,
      this.environment
    );

    let { endpointCode } = this.field.props;
    if (!endpointCode) {
      endpointCode = this.environment.endpointCodes?.companyTin;
    }

    // Attach the user's answer using a hidden meta-key
    const verifyPayload = {
      ...this.originalFetchPayload,
      __verifyPayloadValue: (this.dppInputControl.value || '')
        .toString()
        .trim(),
    };

    const params = {
      endpointCode,
      callerId: uuidv4(),
      payload: verifyPayload,
      requester: uuidv4(),
    };

    this.subscriptions.add(
      this.http
        .post<Record<string, unknown>>(request_url, params)
        .pipe(
          finalize(() => {
            this.isVerifyingDPP = false;
            this.cd.detectChanges();
          })
        )
        .subscribe({
          next: (res: Record<string, unknown>) => {
            const dppStatus = this.checkDppStatus(res);

            // If the backend says mismatch, keep the modal open and show the error
            if (
              dppStatus === 'DATA_REQUIRE_VALIDATION' ||
              dppStatus === 'PROVIDED_INFORMATION_MISMATCH_ERROR'
            ) {
              this.dppError =
                'Verification failed. The provided information does not match our records.';
              this.emitFetchOutcome('failure', {
                message: 'PROVIDED_INFORMATION_MISMATCH_ERROR',
                code: 'DPP_MISMATCH',
              });
              return;
            }
            modal.close('verified');
            this.processSuccessfulFetch(res);
          },
          error: (err: HttpErrorResponse) => {
            const errPayload = err?.error || {};
            const errString = JSON.stringify(errPayload);
            const isDataRequireValidation =
              errPayload.message === 'DATA_REQUIRE_VALIDATION' ||
              errPayload.responseMessage === 'DATA_REQUIRE_VALIDATION' ||
              errPayload.responseCode === 'DATA_REQUIRE_VALIDATION' ||
              errString.includes('DATA_REQUIRE_VALIDATION');

            // Failure! Wrong information. Keep modal open.
            if (isDataRequireValidation || err?.status === 400) {
              this.dppError =
                'Verification failed. The provided information does not match our records.';
            } else {
              this.dppError =
                errPayload.message ||
                errPayload.responseMessage ||
                'An unexpected error occurred during verification.';
            }
            this.emitFetchOutcome('failure', err);
          },
        })
    );
  }

  private processSuccessfulFetch(res: Record<string, unknown>) {
    this.statusClass = IdInputStatusEnum.SUCCESS;
    const data = res['data'] as Record<string, unknown>;
    const response = data['response'] as string;
    const resJson = JSON.parse(response);

    this.formStateService.saveFetchedDataKeyInFormState(
      this.field,
      resJson['ResponseObject']
    );

    if (this.fieldsToPopulate.length > 0 && this.field.formControl.valid) {
      const populateForm: FormGroup<any> | FormArray<any> =
        getPopulateReferenceForm(this.field, this.form);
      populateFormFields(resJson['ResponseObject'], this.field, populateForm);
    } else {
      this.field.options?.detectChanges?.(this.field);
    }
    this.cd.detectChanges();
    this.emitFetchOutcome('success');
  }

  private checkDppStatus(res: Record<string, unknown>): string | null {
    try {
      const data = res['data'] as Record<string, unknown>;
      const responseData =
        data?.['response'] === undefined ? data : data['response'];
      const parsed =
        typeof responseData === 'string'
          ? JSON.parse(responseData)
          : responseData;
      return parsed?.dppStatus || null;
    } catch (e) {
      console.error('Failed to parse DPP status:', e);
      return null;
    }
  }

  getTINFromPrefetchUrl() {
    /**
     * No data fetch when in RFA mode and  not flagged in RFA
     */
    if (
      this.formStateService.checkIfApplicationInAnRFAState() &&
      !this.formStateService
        .getCurrentFormlyFormState()
        ?.[EIremboFormlyFormStateKeys.RFA_FLAGGED_FIELDS]?.includes(
          <string>this.field.key
        )
    ) {
      return;
    }

    let url;
    if (!this.applicantUserId) {
      url = updateUrlWithApiGatewayBaseUrl(
        '/application/v1/user/businesses',
        this.environment
      );
    } else {
      url = updateUrlWithApiGatewayBaseUrl(
        '/application/v1/user/businesses-by-user-id/' + this.applicantUserId,
        this.environment
      );
    }
    this.statusClass = IdInputStatusEnum.VERIFYING;

    this.subscriptions.add(
      this.http.get<IHttpPagedResponse<any>>(url, { headers: {} }).subscribe({
        next: res => {
          const businesses = res.data.content;
          let business = null;
          if (businesses.length > 0) {
            business = businesses.find(
              (b: any) => b.guid === this.applicationBusinessId
            );

            if (business) {
              this.field.formControl.setValue(business.tinNumber);
              this.onVerifyTin(business.tinNumber);
              return;
            }
          }

          /**
           * Enable field without data fetch if in RFA mode and also flagged in RFA
           */
          if (this.formStateService.checkIfApplicationInAnRFAState()) {
            this.isReadonly = business?.tinNumber
              ? true
              : !this.formStateService
                  .getCurrentFormlyFormState()
                  ?.[EIremboFormlyFormStateKeys.RFA_FLAGGED_FIELDS]?.includes(
                    <string>this.field.key
                  );

            this.statusClass = undefined;
            this.cd.detectChanges();
            return;
          }

          this.isReadonly = false;
          if (this.field.formControl.value) {
            this.statusClass = IdInputStatusEnum.DANGER;
          } else {
            this.statusClass = undefined;
          }
          this.cd.detectChanges();
        },
        error: () => {
          this.statusClass = IdInputStatusEnum.DANGER;
          this.cd.detectChanges();
        },
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
